import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Control, FieldErrors, UseFormClearErrors } from 'react-hook-form';
import copy from 'copy-to-clipboard';
import {
  Account,
  Balance,
  BaseellaStatus,
  Currency,
  InternalTransaction,
  InternalTransactionGeneral,
} from '../types';
import IconLoader from '../../icons/loader';
import formatDate from '../../../helpers/format-date';
import formatBalance from '../../../helpers/format-balance';
import { PermissionEnum } from '../../../base-types';
import SidepanelDateRow from '../sidepanel-date-row';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import { SelectItemProps } from '../../select/types';
import ClickOutsideDetector from '../../autocomplete/click-outside-detector';
import IconDown from '../../icons/down';
import SidepanelAmountRow from '../sidepanel-amount-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelTextRow from '../sidepanel-text-row';
import SidepanelTextareaRow from '../sidepanel-textarea-row';
import ButtonPseudo from '../../button/button-pseudo';
import IconRefresh from '../../icons/refresh';
import IconOverdraft from '../../icons/overdraft';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import IconCopy from '../../icons/copy-two';

import styles from '../transaction.module.css';

interface InternalGeneralContentProps {
  general?: InternalTransactionGeneral;
  isLoading?: boolean;
  feeValue?: number;
  withTransactionUrl?: boolean;
  transactionUrlType?: string;
  transactionId?: string;
  currenciesList?: Currency[];
  baseCurrencyName?: string;
  activePermissions?: PermissionEnum[];
  control?: Control;
  fields?: any;
  isEditable?: boolean;
  errors?: FieldErrors<InternalTransaction>;
  senderAccounts?: SelectItemProps[];
  recipientAccounts?: SelectItemProps[];
  senderAccountsIsLoading?: boolean;
  recipientAccountsIsLoading?: boolean;
  baseellaStatus?: BaseellaStatus;
  onSearchAutocomplete?: (name: string, search: string) => void;
  senderAccountBalance?: number;
  recipientAccountBalance?: number;
  currencyId?: string;
  senderAccountBalances?: Balance[];
  recipientAccountBalances?: Balance[];
  senderAccountBalancesIsLoading?: boolean;
  recipientAccountBalancesIsLoading?: boolean;
  overdraftEnabled?: boolean;
  recipientAccountsWithInfo?: Account[];
  senderAccountsWithInfo?: Account[];
  availableCurrencies?: SelectItemProps[];
  amount?: string;
  setAmount?: (data?: string) => void;
  allCurrenciesList?: SelectItemProps[];
  currencyName?: string;
  feeText?: string;
  updateSenderBalance?: () => void;
  updateRecipientBalance?: () => void;
  transferDate?: string;
  clearErrors?: UseFormClearErrors<InternalTransaction>;
}

let senderTimeout: NodeJS.Timeout;
let recipientTimeout: NodeJS.Timeout;

export default function InternalGeneralContent({
  general,
  isLoading,
  withTransactionUrl,
  transactionUrlType,
  transactionId,
  currenciesList,
  control,
  fields,
  isEditable,
  errors,
  senderAccounts,
  recipientAccounts,
  senderAccountsIsLoading,
  recipientAccountsIsLoading,
  baseellaStatus,
  onSearchAutocomplete,
  senderAccountBalance,
  recipientAccountBalance,
  currencyId,
  senderAccountBalances,
  recipientAccountBalances,
  senderAccountBalancesIsLoading,
  recipientAccountBalancesIsLoading,
  overdraftEnabled,
  recipientAccountsWithInfo,
  senderAccountsWithInfo,
  availableCurrencies,
  amount,
  setAmount,
  allCurrenciesList,
  currencyName,
  feeText,
  updateSenderBalance,
  updateRecipientBalance,
  transferDate,
  clearErrors,
}: InternalGeneralContentProps): React.JSX.Element {
  const [senderBalanceValueVisible, setSenderBalanceValueVisible] =
    useState(false);
  const [recipientBalanceValueVisible, setRecipientBalanceValueVisible] =
    useState(false);
  const [maxSenderValueWidth, setMaxSenderValueWidth] = useState(0);
  const [maxRecipientValueWidth, setMaxRecipientValueWidth] = useState(0);
  const [senderBalanceDetailsOnFocus, setSenderBalanceDetailsOnFocus] =
    useState(false);
  const [recipientBalanceDetailsOnFocus, setRecipientBalanceDetailsOnFocus] =
    useState(false);
  const [currentSenderAccount, setCurrentSenderAccount] = useState<Account>();
  const [currentRecipientAccount, setCurrentRecipientAccount] =
    useState<Account>();
  const [currentSenderAccountBalances, setCurrentSenderAccountBalances] =
    useState<Balance>();
  const [currentRecipientAccountBalances, setCurrentRecipientAccountBalances] =
    useState<Balance>();
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [infoValue, setInfoValue] = useState('');

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  let content;

  const enableEdit = Boolean(control);

  useEffect(() => {
    if (general && general.info) {
      setInfoValue(general.info);
    }
  }, [general]);

  useEffect(() => {
    if (general) {
      setCurrentSenderAccount(
        senderAccountsWithInfo?.find(
          (item) => item.fullNumber === general?.senderAccountNumber,
        ),
      );

      setCurrentRecipientAccount(
        recipientAccountsWithInfo?.find(
          (item) => item.fullNumber === general?.recipientAccountNumber,
        ),
      );
    }
  }, [general, recipientAccountsWithInfo, senderAccountsWithInfo]);

  useEffect(() => {
    if (!general && senderAccountsWithInfo && fields.senderAccountNumber) {
      setCurrentSenderAccount(
        senderAccountsWithInfo?.find(
          (item) => item.fullNumber === fields?.senderAccountNumber,
        ),
      );
    }
  }, [general, senderAccountsWithInfo, fields]);

  useEffect(() => {
    if (
      !general &&
      recipientAccountsWithInfo &&
      fields.recipientAccountNumber
    ) {
      setCurrentRecipientAccount(
        recipientAccountsWithInfo?.find(
          (item) => item.fullNumber === fields?.recipientAccountNumber,
        ),
      );
    }
  }, [general, recipientAccountsWithInfo, fields]);

  useEffect(() => {
    setCurrentSenderAccountBalances(
      senderAccountBalances?.find(
        (item) =>
          item.currencyId === general?.currencyId ||
          item.currencyId === currencyId,
      ),
    );

    setCurrentRecipientAccountBalances(
      recipientAccountBalances?.find(
        (item) =>
          item.currencyId === general?.currencyId ||
          item.currencyId === currencyId,
      ),
    );
  }, [
    general,
    senderAccountBalances,
    recipientAccountBalances,
    fields,
    currencyId,
  ]);

  useEffect(() => {
    if (senderBalanceValueVisible && !senderBalanceDetailsOnFocus) {
      senderTimeout = setTimeout(() => {
        setSenderBalanceValueVisible(false);
        setMaxSenderValueWidth(0);
      }, 3000);
    }

    if (senderBalanceDetailsOnFocus) {
      clearTimeout(senderTimeout);
    }
  }, [senderBalanceDetailsOnFocus, senderBalanceValueVisible]);

  useEffect(() => {
    if (recipientBalanceValueVisible && !recipientBalanceDetailsOnFocus) {
      recipientTimeout = setTimeout(() => {
        setRecipientBalanceValueVisible(false);
        setMaxRecipientValueWidth(0);
      }, 3000);
    }

    if (recipientBalanceDetailsOnFocus) {
      clearTimeout(recipientTimeout);
    }
  }, [recipientBalanceValueVisible, recipientBalanceDetailsOnFocus]);

  useEffect(() => {
    if (recipientBalanceValueVisible) {
      const valuesFrom = document.querySelectorAll('.value-to');

      if (valuesFrom && valuesFrom.length) {
        const valuesWidth: number[] = [];

        valuesFrom?.forEach((item) => {
          valuesWidth.push(item.clientWidth);
        });

        setMaxRecipientValueWidth(Math.max.apply(null, valuesWidth));
      }
    }
  }, [recipientBalanceValueVisible]);

  useEffect(() => {
    if (senderBalanceValueVisible) {
      const valuesTo = document.querySelectorAll('.value-from');

      if (valuesTo && valuesTo.length) {
        const valuesWidth: number[] = [];

        valuesTo?.forEach((item) => {
          valuesWidth.push(item.clientWidth);
        });

        setMaxSenderValueWidth(Math.max.apply(null, valuesWidth));
      }
    }
  }, [senderBalanceValueVisible]);

  const availableCurrenciesList = general
    ? allCurrenciesList
    : availableCurrencies;

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    let currencyValue = '';

    if (!control) {
      currencyValue = currencyName as string;
    } else if (enableEdit) {
      currencyValue = general?.currencyId as string;
    }

    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>General Info</div>
          {general && (
            <SidepanelDateRow
              title="Creation Date"
              value={enableEdit ? '' : formatDate(general?.createdAt || '')}
              name="createdAt"
              placeholder="Select Date"
              control={control}
              enableEdit={enableEdit}
              copyEnabled={Boolean(general)}
              copyValue={
                enableEdit
                  ? formatDate(fields?.createdAt || '')
                  : formatDate(general?.createdAt || '')
              }
              disabled
              tabIndex={-1}
            />
          )}
          <SidepanelDateRow
            title="Transfer Date"
            value={enableEdit ? '' : formatDate(general?.transferDate || '')}
            name="transferDate"
            placeholder="Select Date"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(general)}
            error={errors?.transferDate?.message}
            tabIndex={!isEditable || Boolean(general) ? -1 : 0}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? formatDate(fields?.transferDate || '')
                : formatDate(general?.transferDate || '')
            }
            clearErrors={clearErrors}
          />
          <SidepanelDateRow
            title="Transaction Posting date"
            value={
              enableEdit
                ? ''
                : formatDate(general?.depositDateOfDestinationAccount || '')
            }
            name="depositDateOfDestinationAccount"
            placeholder="Select Transaction Posting date"
            control={control}
            enableEdit={enableEdit}
            tabIndex={!isEditable || !transferDate || Boolean(general) ? -1 : 0}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? formatDate(fields?.depositDateOfDestinationAccount || '')
                : formatDate(general?.depositDateOfDestinationAccount || '')
            }
            disabled={!isEditable || !transferDate || Boolean(general)}
            error={errors?.depositDateOfDestinationAccount?.message}
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Sender account"
            value={enableEdit ? '' : general?.senderAccountNumber}
            name="senderAccountNumber"
            placeholder="Choose Sender Account"
            autocompleteList={senderAccounts}
            isLoading={senderAccountsIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(
                general && baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
              )
            }
            tabIndex={
              !isEditable ||
              Boolean(
                general && baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
              ) ||
              Boolean(general)
                ? -1
                : 0
            }
            error={errors?.senderAccountNumber?.message}
            onSearchAutocomplete={onSearchAutocomplete}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? fields?.senderAccountNumber
                : general?.senderAccountNumber
            }
            clearErrors={clearErrors}
            withSuffix
          />
          {enableEdit && fields.senderAccountNumber && !currencyId && (
            <div className={styles.internalBalanceWrapper}>
              <div
                className={`${styles.internalBalanceValue} ${styles.internalBalanceSelectCurrency}`}
              >
                select currency
              </div>
            </div>
          )}
          {enableEdit &&
            senderAccountBalance !== undefined &&
            senderAccountBalance?.toString() &&
            currencyId && (
              <div className={styles.internalBalanceWrapper}>
                {(!overdraftEnabled ||
                  (currentSenderAccount &&
                    (!currentSenderAccount?.overdraftEnabled ||
                      !currentSenderAccountBalances?.overdraftEnabled))) && (
                  <div className={styles.internalBalanceValue}>
                    {formatBalance(
                      senderAccountBalance || 0,
                      currenciesList?.find((item) => item.id === currencyId),
                    )}
                  </div>
                )}
                {overdraftEnabled &&
                  currentSenderAccount?.overdraftEnabled &&
                  currentSenderAccountBalances?.overdraftEnabled && (
                    <div className={styles.exchangeBalance}>
                      <ClickOutsideDetector
                        setOptionsListVisible={() => {
                          setSenderBalanceValueVisible(false);
                          setMaxSenderValueWidth(0);
                          setSenderBalanceDetailsOnFocus(false);
                        }}
                      >
                        <span
                          className={`${styles.balanceValue} ${styles.internalOverdraftBalance}`}
                          onMouseEnter={() =>
                            setSenderBalanceValueVisible(true)
                          }
                        >
                          {!senderAccountBalancesIsLoading && (
                            <>
                              <div className={styles.overdraftHint}>
                                <IconOverdraft />
                              </div>
                              {currentSenderAccountBalances
                                ? formatBalance(
                                    currentSenderAccountBalances?.balanceWithOverdraft ||
                                      0,
                                    currenciesList?.find(
                                      (item) => item.id === currencyId,
                                    ),
                                  )
                                : ''}
                            </>
                          )}

                          {senderAccountBalancesIsLoading && (
                            <IconLoader
                              width={12}
                              height={12}
                              className={styles.overdraftBalanceLoader}
                            />
                          )}
                          {!senderAccountBalancesIsLoading && <IconDown />}
                          {!senderAccountBalancesIsLoading &&
                            currentSenderAccountBalances &&
                            senderBalanceValueVisible && (
                              <div
                                className={styles.balanceDetails}
                                onMouseEnter={() =>
                                  setSenderBalanceDetailsOnFocus(true)
                                }
                                onMouseLeave={() => {
                                  setSenderBalanceValueVisible(false);
                                  setMaxSenderValueWidth(0);
                                  setSenderBalanceDetailsOnFocus(false);
                                }}
                              >
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-from`}
                                    style={{
                                      width: maxSenderValueWidth
                                        ? `${maxSenderValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      currentSenderAccountBalances?.value || 0,
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>Own Funds</div>
                                </div>
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-from`}
                                    style={{
                                      width: maxSenderValueWidth
                                        ? `${maxSenderValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      // eslint-disable-next-line no-nested-ternary
                                      currentSenderAccountBalances?.value < 0
                                        ? (currentSenderAccountBalances?.overdraftValue as number) -
                                            Math.abs(
                                              currentSenderAccountBalances?.value,
                                            ) <
                                          0
                                          ? 0
                                          : (currentSenderAccountBalances?.overdraftValue as number) -
                                            Math.abs(
                                              currentSenderAccountBalances?.value,
                                            )
                                        : (currentSenderAccountBalances?.overdraftValue as number),
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>
                                    Remaining Overdraft
                                  </div>
                                </div>
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-from`}
                                    style={{
                                      width: maxSenderValueWidth
                                        ? `${maxSenderValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      currentSenderAccountBalances?.overdraftValue ||
                                        0,
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>
                                    Overdraft Limit
                                  </div>
                                </div>
                              </div>
                            )}
                        </span>
                      </ClickOutsideDetector>
                    </div>
                  )}
                {updateSenderBalance && (
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={updateSenderBalance}
                    className={`${styles.internalUpdateBalanceButton} ${
                      senderAccountBalancesIsLoading ? styles.loading : ''
                    } `}
                    disabled={senderAccountBalance === undefined || !isEditable}
                    tabIndex={-1}
                  />
                )}
              </div>
            )}
          {!enableEdit && (
            <>
              {(!overdraftEnabled ||
                (currentSenderAccount &&
                  (!currentSenderAccount?.overdraftEnabled ||
                    !currentSenderAccountBalances?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {senderAccountBalance !== undefined
                    ? `${formatBalance(
                        senderAccountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${general?.currency?.name}`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          senderAccountBalance !== undefined
                            ? senderAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentSenderAccount?.overdraftEnabled &&
                currentSenderAccountBalances?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {senderAccountBalance !== undefined
                        ? `${formatBalance(
                            senderAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${general?.currency?.name}`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              senderAccountBalance !== undefined
                                ? senderAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {currentSenderAccountBalances &&
                      currentSenderAccountBalances?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentSenderAccountBalances?.value < 0
                              ? (currentSenderAccountBalances?.overdraftValue as number) -
                                  Math.abs(
                                    currentSenderAccountBalances?.value,
                                  ) <
                                0
                                ? 0
                                : (currentSenderAccountBalances?.overdraftValue as number) -
                                  Math.abs(currentSenderAccountBalances?.value)
                              : (currentSenderAccountBalances?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrenciesList?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {currentSenderAccountBalances &&
                      currentSenderAccountBalances?.overdraftEnabled
                        ? `${formatBalance(
                            currentSenderAccountBalances?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrenciesList?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          <SidepanelAutocompleteRow
            title="Recipient account"
            value={enableEdit ? '' : general?.recipientAccountNumber}
            name="recipientAccountNumber"
            placeholder="Choose Recipient Account"
            autocompleteList={recipientAccounts}
            isLoading={recipientAccountsIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              Boolean(
                general && baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
              )
            }
            tabIndex={
              !isEditable ||
              Boolean(
                general && baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
              ) ||
              Boolean(general)
                ? -1
                : 0
            }
            error={errors?.recipientAccountNumber?.message}
            onSearchAutocomplete={onSearchAutocomplete}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? fields?.recipientAccountNumber
                : general?.recipientAccountNumber
            }
            clearErrors={clearErrors}
            withSuffix
          />
          {enableEdit && fields.recipientAccountNumber && !currencyId && (
            <div className={styles.internalBalanceWrapper}>
              <div
                className={`${styles.internalBalanceValue} ${styles.internalBalanceSelectCurrency}`}
              >
                select currency
              </div>
            </div>
          )}
          {enableEdit &&
            recipientAccountBalance !== undefined &&
            recipientAccountBalance?.toString() &&
            currencyId && (
              <div className={styles.internalBalanceWrapper}>
                {(!overdraftEnabled ||
                  (currentRecipientAccount &&
                    (!currentRecipientAccount?.overdraftEnabled ||
                      !currentRecipientAccountBalances?.overdraftEnabled))) && (
                  <div className={styles.internalBalanceValue}>
                    {formatBalance(
                      recipientAccountBalance || 0,
                      currenciesList?.find((item) => item.id === currencyId),
                    )}
                  </div>
                )}
                {overdraftEnabled &&
                  currentRecipientAccount?.overdraftEnabled &&
                  currentRecipientAccountBalances?.overdraftEnabled && (
                    <div className={styles.exchangeBalance}>
                      <ClickOutsideDetector
                        setOptionsListVisible={() => {
                          setRecipientBalanceValueVisible(false);
                          setMaxRecipientValueWidth(0);
                          setRecipientBalanceDetailsOnFocus(false);
                        }}
                      >
                        <span
                          className={`${styles.balanceValue} ${styles.internalOverdraftBalance}`}
                          onMouseEnter={() =>
                            setRecipientBalanceValueVisible(true)
                          }
                        >
                          {!recipientAccountBalancesIsLoading && (
                            <>
                              <div className={styles.overdraftHint}>
                                <IconOverdraft />
                              </div>
                              {currentRecipientAccount
                                ? formatBalance(
                                    currentRecipientAccountBalances?.balanceWithOverdraft ||
                                      0,
                                    currenciesList?.find(
                                      (item) => item.id === currencyId,
                                    ),
                                  )
                                : ''}
                            </>
                          )}

                          {recipientAccountBalancesIsLoading && (
                            <IconLoader
                              width={12}
                              height={12}
                              className={styles.overdraftBalanceLoader}
                            />
                          )}
                          {!recipientAccountBalancesIsLoading && <IconDown />}
                          {!recipientAccountBalancesIsLoading &&
                            currentRecipientAccountBalances &&
                            recipientBalanceValueVisible && (
                              <div
                                className={styles.balanceDetails}
                                onMouseEnter={() =>
                                  setRecipientBalanceDetailsOnFocus(true)
                                }
                                onMouseLeave={() => {
                                  setRecipientBalanceValueVisible(false);
                                  setMaxRecipientValueWidth(0);
                                  setRecipientBalanceDetailsOnFocus(false);
                                }}
                              >
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-to`}
                                    style={{
                                      width: maxRecipientValueWidth
                                        ? `${maxRecipientValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      currentRecipientAccountBalances?.value ||
                                        0,
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>Own Funds</div>
                                </div>
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-to`}
                                    style={{
                                      width: maxRecipientValueWidth
                                        ? `${maxRecipientValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      // eslint-disable-next-line no-nested-ternary
                                      currentRecipientAccountBalances?.value < 0
                                        ? (currentRecipientAccountBalances?.overdraftValue as number) -
                                            Math.abs(
                                              currentRecipientAccountBalances?.value,
                                            ) <
                                          0
                                          ? 0
                                          : (currentRecipientAccountBalances?.overdraftValue as number) -
                                            Math.abs(
                                              currentRecipientAccountBalances?.value,
                                            )
                                        : (currentRecipientAccountBalances?.overdraftValue as number),
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>
                                    Remaining Overdraft
                                  </div>
                                </div>
                                <div className={styles.balanceDetailsValue}>
                                  <div
                                    className={`${styles.value} value-to`}
                                    style={{
                                      width: maxRecipientValueWidth
                                        ? `${maxRecipientValueWidth}px`
                                        : 'auto',
                                    }}
                                  >
                                    {formatBalance(
                                      currentRecipientAccountBalances?.overdraftValue ||
                                        0,
                                      currenciesList?.find(
                                        (item) => item.id === currencyId,
                                      ),
                                    )}
                                  </div>
                                  <div className={styles.title}>
                                    Overdraft Limit
                                  </div>
                                </div>
                              </div>
                            )}
                        </span>
                      </ClickOutsideDetector>
                    </div>
                  )}
                {updateRecipientBalance && (
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={updateRecipientBalance}
                    className={`${styles.internalUpdateBalanceButton} ${
                      recipientAccountBalancesIsLoading ? styles.loading : ''
                    } `}
                    disabled={
                      recipientAccountBalance === undefined || !isEditable
                    }
                    tabIndex={-1}
                  />
                )}
              </div>
            )}
          {!enableEdit && (
            <>
              {(!overdraftEnabled ||
                (currentRecipientAccount &&
                  (!currentRecipientAccount?.overdraftEnabled ||
                    !currentRecipientAccountBalances?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {recipientAccountBalance !== undefined
                    ? `${formatBalance(
                        recipientAccountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${general?.currency?.name}`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          recipientAccountBalance !== undefined
                            ? recipientAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentRecipientAccount?.overdraftEnabled &&
                currentRecipientAccountBalances?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {recipientAccountBalance !== undefined
                        ? `${formatBalance(
                            recipientAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${general?.currency?.name}`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              recipientAccountBalance !== undefined
                                ? recipientAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {currentRecipientAccountBalances &&
                      currentRecipientAccountBalances?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentRecipientAccountBalances?.value < 0
                              ? (currentRecipientAccountBalances?.overdraftValue as number) -
                                  Math.abs(
                                    currentRecipientAccountBalances?.value,
                                  ) <
                                0
                                ? 0
                                : (currentRecipientAccountBalances?.overdraftValue as number) -
                                  Math.abs(
                                    currentRecipientAccountBalances?.value,
                                  )
                              : (currentRecipientAccountBalances?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrenciesList?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {currentRecipientAccountBalances &&
                      currentRecipientAccountBalances?.overdraftEnabled
                        ? `${formatBalance(
                            currentRecipientAccountBalances?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrenciesList?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelAmountRow
                title="Amount"
                value={enableEdit ? '' : general?.sum.toFixed(2)}
                name="sum"
                placeholder="Enter amount"
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(general)}
                error={errors?.sum?.message}
                tabIndex={!isEditable || Boolean(general) ? -1 : 0}
                amount={amount}
                setAmount={setAmount}
                currenciesList={currenciesList}
                currencyId={currencyId}
                copyEnabled={Boolean(general)}
                copyValue={enableEdit ? fields?.sum : general?.sum}
                clearErrors={clearErrors}
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelSelectRow
                title="Currency"
                value={currencyValue}
                name="currencyId"
                placeholder="Choose currency"
                control={control}
                selectList={availableCurrenciesList}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(general)}
                tabIndex={!isEditable || Boolean(general) ? -1 : 0}
                error={errors?.currencyId?.message}
                copyEnabled={Boolean(general)}
                copyValue={
                  enableEdit
                    ? availableCurrencies?.find(
                        (item) => item.value === currencyValue,
                      )?.name
                    : general?.currency?.name
                }
                clearErrors={clearErrors}
              />
            </div>
          </div>
          {feeText && !transactionId && (
            <div className={styles.fee}>{feeText}</div>
          )}
        </div>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Details</div>
          <SidepanelTextRow
            title="Document #"
            value={enableEdit ? '' : general?.documentNumber}
            name="documentNumber"
            placeholder="Enter Document"
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            error={errors?.documentNumber?.message}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit ? fields?.documentNumber : general?.documentNumber
            }
            clearErrors={clearErrors}
          />
        </div>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Payment details</div>
          <SidepanelTextareaRow
            title="Payment Details"
            value={enableEdit ? '' : general?.info}
            name="info"
            placeholder="Enter Payment Details"
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            error={errors?.info?.message}
            copyEnabled={Boolean(general)}
            copyValue={enableEdit ? fields?.info : general?.info}
            fieldValue={infoValue}
            setFieldValue={setInfoValue}
            maxLength={140}
            clearErrors={clearErrors}
          />
        </div>
        {withTransactionUrl && (
          <NavLink
            to={`/transactions/${transactionUrlType}/${transactionId}`}
            target="_blank"
            className={`${styles.transactionLink} ${styles.withMargin}`}
            tabIndex={-1}
          >
            Go to transaction
          </NavLink>
        )}
      </>
    );
  }

  return content;
}
